import React, { useState, useRef } from "react"
import readXlsxFile from "read-excel-file"
import BaseApi from "../../api/api"
import { NotificationService } from "../../features/NotificationService/NotificationService"
import { useSelector } from "react-redux"
import { selectWbContent } from "../../app/reducers/contentSlice"
import { StatusInfo } from "../UpdateStoksIcon/StatusInfo/StatusInfo"
import { Modal } from "../../features/Modal/Modal"
import { ObjectViewer } from "../ObjectViewer/ObjectViewer"
import { STATUS } from "../../utils/const"
import {
  Box,
  Flex,
  Button,
  Card,
  Heading,
  Text,
  IconButton,
  ScrollArea,
} from "@radix-ui/themes"
import { Cross1Icon, UploadIcon, TrashIcon } from "@radix-ui/react-icons"
import { Select } from "../Select/Select"

interface ExcelData {
  [key: string]: any[]
}

export const CardsToRemove = () => {
  const [file, setFile] = useState<File | null>(null)
  const [columns, setColumns] = useState<string[]>([])
  const [selectedColumn, setSelectedColumn] = useState<string>("")
  const [columnData, setColumnData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setModalOpen] = useState(false)
  const [excelData, setExcelData] = useState<ExcelData | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const wbContentState = useSelector(selectWbContent)

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    setIsLoading(true)

    try {
      const data = await readXlsxFile(selectedFile)

      if (data.length > 0) {
        const headers = data[0] as string[]
        setColumns(headers)

        const parsedData: ExcelData = {}
        headers.forEach((header, index) => {
          parsedData[header] = data.slice(1).map((row) => row[index])
        })

        setExcelData(parsedData)
      }
    } catch (error) {
      console.error("Ошибка при чтении файла:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleColumnSelect = (column: string) => {
    setSelectedColumn(column)

    if (column && excelData) {
      const data = excelData[column] || []
      setColumnData(
        data.filter(
          (item) => item !== null && item !== undefined && item !== "",
        ),
      )
    }
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const clearData = () => {
    setFile(null)
    setColumns([])
    setSelectedColumn("")
    setColumnData([])
    setExcelData(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const submitData = async () => {
    if (columnData.length) {
      try {
        const response = await BaseApi.cardsToTrashWB(columnData)
        const data = response?.data
        const success = data?.success || false
        NotificationService.showNotification({
          message:
            data?.message || (success ? "Процесс удаления запущен" : "Ошибка"),
          level: success ? "success" : "error",
          success,
        })
        if (success) {
          clearData()
        }
      } catch (error: any) {
        NotificationService.showNotification({
          message: error?.response?.data?.message || "Ошибка при удалении",
          level: "error",
          success: false,
        })
      }
    }
  }

  if (wbContentState.deleteCardStatus === STATUS.IN_PROGRESS) {
    return (
      <Box p="4" maxWidth="700px" mx="auto">
        <StatusInfo status={wbContentState.deleteCardStatus as any} />
      </Box>
    )
  }

  return (
    <Box p="4" maxWidth="700px" mx="auto">
      <Heading size="5" align="center" mb="5">
        Перенос карточек в корзину
      </Heading>

      <Flex align="center" justify="center" gap="4" mb="4" wrap="wrap">
        <Text size="2" color="gray">
          Статус последнего удаления:
        </Text>
        <StatusInfo
          status={
            wbContentState.deleteCardErrors?.length
              ? "SUCCESS_WITH_ERROR"
              : (wbContentState.deleteCardStatus as any)
          }
        />
        {wbContentState.deleteCardErrors?.length ? (
          <>
            <Button
              variant="outline"
              size="1"
              onClick={() => setModalOpen(true)}
            >
              посмотреть ошибки
            </Button>
            <Modal
              isOpen={isModalOpen}
              onClose={() => setModalOpen(false)}
              onOk={() => setModalOpen(false)}
              content={
                <ObjectViewer
                  data={wbContentState.deleteCardErrors}
                  title="Ошибки при удалении карточек"
                />
              }
              hideCancelBtn
            />
          </>
        ) : null}
      </Flex>

      <Flex direction="column" gap="3" mb="4">
        {!file && (
          <Button onClick={handleAttachClick} disabled={isLoading} size="3">
            <UploadIcon />
            {isLoading ? "Загрузка..." : "Прикрепить Excel"}
          </Button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />

        {file && (
          <Flex
            align="center"
            justify="between"
            p="2"
            style={{
              backgroundColor: "var(--gray-3)",
              borderRadius: "var(--radius-2)",
            }}
          >
            <Text size="2">Файл: {file.name}</Text>
            <IconButton
              variant="ghost"
              color="red"
              size="1"
              onClick={clearData}
            >
              <Cross1Icon />
            </IconButton>
          </Flex>
        )}
      </Flex>

      {columns.length > 0 && (
        <Card size="2" style={{ marginBottom: 16 }}>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                Выберите колонку с артикулами:
              </Text>
              <Select
                value={selectedColumn}
                onChange={handleColumnSelect}
                options={columns.map((column) => ({
                  value: column,
                  label: column,
                }))}
                placeholder="-- Выберите колонку --"
              />
            </Flex>
          </Flex>
        </Card>
      )}

      {columnData.length > 0 && (
        <Flex direction="column" gap="4">
          <Card size="2">
            <Flex direction="column" gap="3">
              <Heading size="4">Данные из колонки "{selectedColumn}":</Heading>
              <Text size="2" color="gray">
                Всего элементов: {columnData.length}
              </Text>
              <ScrollArea style={{ maxHeight: 300 }}>
                <Flex direction="column" gap="2">
                  {columnData.slice(0, 10).map((item, index) => (
                    <Card
                      key={index}
                      variant="classic"
                      size="1"
                      style={{ borderLeft: "3px solid var(--accent-9)" }}
                    >
                      <Text size="2">{String(item)}</Text>
                    </Card>
                  ))}
                  {columnData.length > 10 && (
                    <Text
                      size="2"
                      color="gray"
                      align="center"
                      style={{ fontStyle: "italic" }}
                    >
                      ... и еще {columnData.length - 10} элементов
                    </Text>
                  )}
                </Flex>
              </ScrollArea>
            </Flex>
          </Card>

          <Button color="red" size="3" onClick={submitData}>
            <TrashIcon />
            Удалить карточки
          </Button>
        </Flex>
      )}
    </Box>
  )
}
